import houseIcon from "../../assets/icons/my-home.png";
import blockIcon from "../../assets/icons/my-block.png";
import estateIcon from "../../assets/icons/estate.png";
import dividedBlockIcon from "../../assets/icons/divided-block.png";
import { formathourminutes, isNullOrUndefined } from "../formatting";
import { isUndefinedNullOrEmpty } from '../translation/helper';
import moment from "moment";
import { TFunction } from "i18next";
import { catalystTriageContractorType, EMAContractorType } from "../../models/Repair/Contractor";
import { CasePriority, RepairPriorityCode } from "../../models/Repair/CreateRepair";
import { AppointmentAvailability, Bundle, Contractor, WOEnvelope } from "../../models/Repair/Appointment";

export enum HHSRSHazardType {
  DM = "DM",
  EC = "EC",
  EH = "EH",
  EL = "EL",
  FA = "FA",
  FH = "FH",
  HY = "HY",
  PE = "PE",
  ST = "ST",
}

export class Helper {
  static readonly ValidationRegex = {
    NumericOnly: /^[+0-9]+$/, // Regex for numeric input (phone number)
    AlphabetWithSpecialChars: /^[-A-Za-z' \b]+$/, // Regex for name input
    All_Special_Chars_Allowed: /^[A-Za-z0-9\s|.,_@+#%'\-()^`~=¬£€\\/[\]?:;"{}]*$/,//All Special Chars Allowed - .()%@:?@#%:"'',./?|]{}~`-=+
  };

  static readonly LengthValidation = {
    CrimeReferenceNumber: {
      Minimum: 3,
      Maximum: 20,
    }
  } as const;

  static readonly BackValue = {
    RepairAsset: "RepairAsset",
    RepairTradeCategory: "RepairTradeCategory",
    RepairLocation: "RepairLocation",
    Customer360View: "Customer360View",
    Asset360View: "Asset360View",
    AssetWorkOrderResult: "AssetWorkOrderResult",
    Case360View: "Case360View",
    CustomerWorkOrder: "CustomerWorkOrder",
    WorkOrder360View: "WorkOrder360View",
    RepairEDEmergency: "RepairEDEmergency",
    DuplicateRepair: "DuplicateRepair"
  } as const;

  static readonly PreAssessment = {
    CategoryPrefix: "PreAssess-"
  } as const;

  /**
   * True when the repair has been routed through the HHSRS screens, either the
   * Pre-assessment journey or the Auto Hazard journey.
   *
   * This mirrors the two forward routing branches on Repair Additional Details:
   *  - Pre-assessment: isPreAssessmentRequired === true, eligible, toggle enabled
   *  - Auto Hazard:    isPreAssessmentRequired !== true, isHazard === true, toggle enabled
   *
   * It describes the journey the repair travelled through, not the assessment outcome.
   */
  static isHHSRSJourney(parameters: {
    isPreAssessmentRequired?: boolean | null;
    isHazard?: boolean | null;
    isPreAssessmentEligible: boolean;
    isHHSRSRepairEnabled?: boolean | null;
  }): boolean {
    const {
      isPreAssessmentRequired,
      isHazard,
      isPreAssessmentEligible,
      isHHSRSRepairEnabled,
    } = parameters;

    if (isHHSRSRepairEnabled !== true) {
      return false;
    }

    return isPreAssessmentRequired === true
      ? isPreAssessmentEligible
      : isHazard === true;
  }

  /**
   * Single source of truth for hiding the Vulnerability page.
   *
   * Repair Upload Media, Covid Question, Customer Details and Vulnerability Question
   * must all agree, otherwise a hidden Vulnerability page on the forward journey becomes
   * a Back loop when one of the pages disagrees.
   */
  static shouldHideVulnerabilityPage(parameters: {
    hasHideVulnerabilityPermission?: boolean | null;
    isEmergency?: boolean | null;
    isHazard?: boolean | null;
    isHHSRSRepairEnabled?: boolean | null;
  }): boolean {
    const {
      hasHideVulnerabilityPermission,
      isEmergency,
      isHazard,
      isHHSRSRepairEnabled,
    } = parameters;

    return Boolean(
      hasHideVulnerabilityPermission ||
      isEmergency ||
      (isHazard === true && isHHSRSRepairEnabled === true)
    );
  }

  static isPreAssessmentCategory(category?: string | null): boolean {
    return category?.toLowerCase().startsWith(
      Helper.PreAssessment.CategoryPrefix.toLowerCase()
    ) ?? false;
  }

  static isMatchingPreAssessmentCategory(
    category?: string | null,
    expectedCategory?: string | null
  ): boolean {
    if (!category || !expectedCategory) {
      return false;
    }

    return category.toLowerCase() === expectedCategory.toLowerCase();
  }

  static isInformationAlertType(type?: string | null): boolean {
    return type?.toLowerCase() === "information";
  }

  static readonly BackRedirectMap: { [key: string]: string } = {
    RepairAsset: "/RepairAsset",
    RepairTradeCategory: "/RepairTradeCategory",
    RepairLocation: "/RepairLocation",
    Customer360View: "/Customer360View",
    Asset360View: "/Asset360View",
    AssetWorkOrderResult: "/AssetWorkOrderResult",
    Case360View: "/Case360View",
    CustomerWorkOrder: "/CustomerWorkOrder",
    RepairEDEmergency: "/RepairEDEmergency",
    DuplicateRepair: "/DuplicateRepair"
  } as const;

  static readonly HHSRSRepairCauseId = 14;
  static readonly GeneralWearandTear = 1;

  static readonly RepairCauseLabels = {
    1: "General Wear and Tear",
    2: "Complaint",
    3: "Vandalism or Damage",
    4: "Technical Inspection",
    5: "Disrepair",
    6: "Right to Improve",
    7: "Mutual Exchange",
    8: "Leaseholder Improvement Request",
    9: "Estate Management Survey",
    10: "Keysafe Installation",
    11: "Void Survey",
    12: "Damp and Mould Survey",
    14: "Housing Health and Safety Rating System"
  } as const;

  static readonly AssetTypeName = {
    "Block": "Block",
    "Property": "Property",
    "Estate": "Estate",
    "Estate Area": "Estate Area",
    "Divided Block": "Divided Block",
    "Scheme": "Scheme"
  } as const;

  static readonly AssetTypeId = {
    1: "Block",
    2: "Property",
    3: "Estate",
    4: "Estate Area",
    5: "Divided Block",
    6: "Scheme"
  } as const;


  static readonly RepairEnhancedDiagnosticStatus = {
    "Launched": "Launched",
    "Completed": "Completed",
    "Failed": "Failed",
    "Abandoned": "Abandoned"
  } as const;

  static readonly BlockMap = {
    Property: "Property",
    "Divided Block": "Divided Block",
    Block: "Block",
    Estate: "Estate",
    "Estate Area": "Estate",
  };

  static readonly AssetStatuses = {
    Void: "VOID",
    Occupied: "OCCP",
    Closed: "CLSD",
  };

  static readonly RepairPriorityCodes = {
    DM: "DM",
    DME: "DME",
    DML: "DML",
    DMS: "DMS",
    V14: "V14",
    EME: "EME",
    NA: "NA",
    HHW: "HHW"
  } as const;

  static readonly RepairPriorityLabels = {
    [Helper.RepairPriorityCodes.DM]: "Damp and Mould -14 days",
    [Helper.RepairPriorityCodes.DME]: "Damp and Mould -24 hours",
    [Helper.RepairPriorityCodes.DML]: "Damp and Mould Low -28 days",
    [Helper.RepairPriorityCodes.DMS]: "Damp and Mould Significant -14 days",
    [Helper.RepairPriorityCodes.V14]: "Next Available -14 days",
    [Helper.RepairPriorityCodes.EME]: "Emergency",
    [Helper.RepairPriorityCodes.NA]: "Next Available",
    [Helper.RepairPriorityCodes.HHW]: "Heating & Hot Water (Seasonal)"
  } as const;

  static readonly RepairReportingMethods = {
    Telephone: 1,
    Email: 2,
    SocialMedia: 3,
    CustomerSelfServe: 4,
    ColleagueSelfServe: 5
  } as const;

  static readonly RepairReportingMethodLabels = {
    [Helper.RepairReportingMethods.Telephone]: "Telephone",
    [Helper.RepairReportingMethods.Email]: "Email",
    [Helper.RepairReportingMethods.SocialMedia]: "Social media",
    [Helper.RepairReportingMethods.CustomerSelfServe]: "Customer Self-Serve",
    [Helper.RepairReportingMethods.ColleagueSelfServe]: "Colleague Self-Serve"
  } as const;

  static readonly ActiveHousingScriptId = "active-housing-script";

  static removeActiveHousingScript(scriptId: string = Helper.ActiveHousingScriptId): void {
    requestAnimationFrame(() => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    });
  }
  static readonly WOFailureMethod = {
    RepairDetermineContractor: "repair.determineContractor",
    RepairDeterminePriority: "repair.determinePriority",
    DmcDetermineContractor: "dmc.determineContractor",
    DmcDeterminePriority: "dmc.determinePriority",
    DmcCreateWorkOrder: "dmc.createworkorder",
    DmcGetJobNumber: "dmc.getjobnumber",
    DmcAggregator: "dmc.dmcaggregator",
    RepairGetJobNumber: "repair.getjobnumber",
    RepairUpdateWorkOrder: "repair.updateworkorder",
    DmcDefectPostNote: "dmc.defectpostnote",
    RepairGetAvailability: "repair.getavailability",
    DmcGetAvailability: "dmc.getavailability"
  } as const;

  static getDateRangeFromMonthsAgo = (monthsAgo = 6) => {
    const timeValue = formathourminutes();
    const today = new Date();
    const pastDate = new Date();
    pastDate.setMonth(today.getMonth() - monthsAgo);

    const todayYmd = today.toISOString().split("T")[0];
    const pastDateYmd = pastDate.toISOString().split("T")[0];

    const dateFromStr = `${pastDateYmd}T0${timeValue[0]}:${timeValue[1]}:00Z`;
    const dateToStr = `${todayYmd}T${23 - timeValue[0]}:${timeValue[2]}:00Z`;

    return { dateFrom: dateFromStr, dateTo: dateToStr };
  };

  static validateMinLength(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    minLength: number
  ): string | null {
    if (value.length < minLength) {
      return t("minimum_length", {
        field: fieldName,
        min: minLength,
      });
    }
    return null;
  }

  static validateMaxLength(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    maxLength: number
  ): string | null {
    if (value.length > maxLength) {
      return t("maximum_length", {
        field: fieldName,
        max: maxLength,
      });
    }
    return null;
  }

  static validateInvalidCharacters(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    regex: RegExp
  ): string | null {
    if (!regex.test(value)) {
      return t("invalid_characters", {
        field: fieldName,
      });
    }
    return null;
  }

  static readonly MaintenanceResponsibilityGroup = {
    PartiallyPeabody: "PARTIALLY-PEABODY",
    EMA: "EMA",
    NotPeabody: "NotPeabody",
    Peabody: "Peabody"
  };

  static readonly MaintenanceGroupMappings: Record<string, (string | null)[]> = {
    [Helper.MaintenanceResponsibilityGroup.PartiallyPeabody]: ["FMPART", null, "SINGLE"],
    [Helper.MaintenanceResponsibilityGroup.EMA]: ["EMAFULL"],
    [Helper.MaintenanceResponsibilityGroup.NotPeabody]: [
      "FREEHDFULL", "FREEHOLD", "LEASEHOLD", "LEASEHDFULL", "QLNO", "AGENT", "OWNER"
    ],
    [Helper.MaintenanceResponsibilityGroup.Peabody]: ["FMFULL", "QLYES", "DEFAULTED"]
  };

  static getAssetIcon(assetType: string) {
    if (assetType === "Property") return houseIcon;
    if (assetType === "Block") return blockIcon;
    if (assetType === "Divided Block") return dividedBlockIcon;
    if (assetType.includes("Estate")) return estateIcon;
    return houseIcon;
  }

  static doesHavePermission(userPermissions: string[] | null | undefined, permission: string): boolean {
    if (!Array.isArray(userPermissions)) {
      return false;
    }
    return userPermissions.includes(permission);



  }

  static buildAppointmentDisplayFromProps(props?: any): string {
    const s = props?.location?.state || {};
    if (!s?.date) return "";
    const longDate = moment(s.date).format("dddd D MMMM YYYY");
    const label = String(s?.availabilityTypeLabel ?? "").trim();
    const start = String(s?.start ?? "").trim();
    const end = String(s?.end ?? "").trim();
    const range = start && end ? `${start}–${end}` : (start || end);
    const bracket = label && range ? `${label}, ${range}` : (label || range);
    return bracket ? `${longDate} (${bracket})` : longDate;
  }

  static getRepairJourneySummaryCase({
    workOrderObject,
    dmcWorkOrderObject,
    woFailureCases,
    ButtonRequestAnotherRepairValue
  }: {
    workOrderObject: any;
    dmcWorkOrderObject?: any;
    woFailureCases?: Record<string, any> | null;
    ButtonRequestAnotherRepairValue: boolean
  }):
    | "both-success"
    | "rep-success-dmc-fail"
    | "rep-fail-dmc-success"
    | "both-fail"
    | "rep-triage-dmc-success"
    | "rep-triage-dmc-fail"
    | "single-rep-success"
    | "single-rep-success-assessment"
    | "single-rep-fail"
    | "single-rep-triage"
    | "dmc-success"
    | "uncovered" {

    const isOriginalRepairApplicable = !isUndefinedNullOrEmpty(workOrderObject);
    const isMultiWODMRepairApplicable = !isUndefinedNullOrEmpty(dmcWorkOrderObject);
    const isOriginalRepairAndDMApplicable = isMultiWODMRepairApplicable && isOriginalRepairApplicable;

    const hasRepairSuccess = !isUndefinedNullOrEmpty(workOrderObject?.jobNo);
    const hasDmcSuccess = !isUndefinedNullOrEmpty(dmcWorkOrderObject?.jobNo);
    const originalRepairContractor = String(workOrderObject?.contractor ?? "").trim();

    const hasRepairFail =
      !!(woFailureCases && Object.keys(woFailureCases).some((k) => k.startsWith("repair.")));
    const hasDmcFail =
      !!(woFailureCases && Object.keys(woFailureCases).some((k) => k.startsWith("dmc.")));
    const isRepairTriage =
      !hasRepairSuccess &&
      (workOrderObject?.isPotentialDuplicate === true || workOrderObject?.isVulnerable === true);

    const isRepairInDefect = workOrderObject?.isInDefect === true &&
      workOrderObject?.isVandalism === false &&
      workOrderObject?.isCatalyst === false;

    const isRepairInDefectCatalyst = workOrderObject?.isInDefect === true &&
      workOrderObject?.isVandalism === false &&
      workOrderObject?.isCatalyst === true;
    const isRepairCatalystTriage =
      !hasRepairSuccess &&
      (
        originalRepairContractor === catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ||
        originalRepairContractor === catalystTriageContractorType?.CATALYST_TRIAGE_TIER2 ||
        originalRepairContractor === catalystTriageContractorType?.CATALYST_TRIAGE_EMA
      );


    const isUnknownEMA =
      !hasRepairSuccess &&
      (
        originalRepairContractor === EMAContractorType.EMA_UNKNOWN_CONTACTDETAILS_TRIAGE ||
        originalRepairContractor === EMAContractorType.UNKNOWN_EMA_TRIAGE
      );



    //Final page for either
    // 1. repair creation flow with no DM associatedWOs
    // 2. select appointment flow from WO360 view for non DM Muti WO flow
    // 3. select appointment flow from WO360 view for PRODAMP without any associatedWOs
    if (!isOriginalRepairAndDMApplicable && isOriginalRepairApplicable && hasRepairSuccess && ButtonRequestAnotherRepairValue && !workOrderObject?.isPendingAssessment) return "single-rep-success-assessment";
    if (!isOriginalRepairAndDMApplicable && isOriginalRepairApplicable && hasRepairSuccess) return "single-rep-success";
    if (!isOriginalRepairAndDMApplicable && isOriginalRepairApplicable && hasRepairFail) return "single-rep-fail";
    if (!isOriginalRepairAndDMApplicable && isOriginalRepairApplicable && (isRepairTriage || isRepairCatalystTriage)) return "single-rep-triage";

    //Final page for select appointment flow from WO360 view for PRODAMP with associatedWOs
    if (!isOriginalRepairAndDMApplicable && isMultiWODMRepairApplicable && hasDmcSuccess) return "dmc-success";

    if (isUnknownEMA) return "single-rep-success";
    //Final page for repair creation flow with DM associatedWOs
    if (isOriginalRepairAndDMApplicable && hasRepairSuccess && hasDmcSuccess) return "both-success";
    if (isOriginalRepairAndDMApplicable && hasRepairSuccess && hasDmcFail) return "rep-success-dmc-fail";
    if (isOriginalRepairAndDMApplicable && hasRepairFail && hasDmcSuccess) return "rep-fail-dmc-success";
    if (isOriginalRepairAndDMApplicable && (hasRepairFail || !hasRepairSuccess) && (hasDmcFail || !hasDmcSuccess) && !isRepairTriage) return "both-fail";
    if (isOriginalRepairAndDMApplicable && (isRepairTriage || isRepairCatalystTriage) && hasDmcSuccess) return "rep-triage-dmc-success";
    if (isOriginalRepairAndDMApplicable && (isRepairTriage || isRepairCatalystTriage) && !hasDmcSuccess) return "rep-triage-dmc-fail";

    if (isRepairInDefect && hasRepairSuccess && !hasDmcSuccess && !isMultiWODMRepairApplicable) return "single-rep-success";
    if (isRepairInDefectCatalyst && !hasRepairSuccess && !hasDmcSuccess && !isOriginalRepairAndDMApplicable) return "single-rep-success";
    return "uncovered";

  }

  static getRepairJourneySummary(
    summaryCase:
      | "both-success"
      | "rep-success-dmc-fail"
      | "rep-fail-dmc-success"
      | "both-fail"
      | "rep-triage-dmc-success"
      | "rep-triage-dmc-fail"
      | "single-rep-success"
      | "single-rep-success-assessment"
      | "single-rep-triage"
      | "single-rep-fail"
      | "dmc-success"
      | "uncovered",
    icons: { successIcon: any; failIcon: any; partialIcon: any }
  ): { headingKey: any; subKey?: any; icon: any } {
    const { successIcon, failIcon, partialIcon } = icons;
    return (
      {
        "single-rep-success-assessment": {
          headingKey: "Assessment_completed",
          subKey: undefined,
          icon: successIcon,
        },
        "single-rep-success": {
          headingKey: "CPR084_RepairsRequested",
          subKey: undefined,
          icon: successIcon,
        },
        "dmc-success": {
          headingKey: "CPR084_RepairsRequested",
          subKey: undefined,
          icon: successIcon,
        },
        "both-success": {
          headingKey: "CPR084_RepairsRequested",
          subKey: undefined,
          icon: successIcon,
        },
        "single-rep-triage": {
          headingKey: "CPR084_PartiallyCompleted",
          subKey: undefined,
          icon: successIcon,
        },
        "rep-success-dmc-fail": {
          headingKey: "CPR084_PartiallyCompleted",
          subKey: "CPR086_RepSuccess_DmcFail",
          icon: partialIcon,
        },
        "rep-fail-dmc-success": {
          headingKey: "CPR084_PartiallyCompleted",
          subKey: "CPR086_RepFail_DmcSuccess",
          icon: partialIcon,
        },
        "both-fail": {
          headingKey: "CPR084_RequestsFailed",
          subKey: "CPR086_BothFail",
          icon: failIcon,
        },
        "rep-triage-dmc-success": {
          headingKey: "CPR084_PartiallyCompleted",
          subKey: "CPR086_RepTriage_DmcSuccess",
          icon: partialIcon,
        },
        "rep-triage-dmc-fail": {
          headingKey: "CPR084_PartiallyCompleted",
          subKey: "CPR086_RepTriage_DmcFail",
          icon: partialIcon,
        },
        "single-rep-fail": {
          headingKey: "CPR084_RequestsFailed",
          subKey: undefined,
          icon: failIcon,
        },
        "uncovered": {
          headingKey: undefined,
          subKey: undefined,
          icon: undefined,
        }
      } as const
    )[summaryCase];
  }

  static buildRepairJourneyConclusionMsgs({
    workOrderObject,
    dmcWorkOrderObject,
    woFailureCases,
    props,
    t,
    hasExistingDMCaseOrWO,
    appointmentBundle,
    hasViewContractorOOHCommunicationInstruction = false,
    ButtonRequestAnotherRepairValue
  }: {
    workOrderObject: any;
    dmcWorkOrderObject?: any;
    woFailureCases?: Record<string, any> | null;
    props: any;
    hasExistingDMCaseOrWO: boolean;
    t: TFunction;
    appointmentBundle?: {
      originalWO?: { appointmentSlotSelected?: any } | null;
      dmc?: { appointmentSlotSelected?: any } | null;
    };
    hasViewContractorOOHCommunicationInstruction?: boolean;
    ButtonRequestAnotherRepairValue: boolean
  }) {
    const rows: Array<{ referenceRow: { title: string; body: string }, nextStepsRow: { title: string; body: string } }> = [];

    const repairContractName = String(workOrderObject?.contractorName ?? "").trim();
    const dmcContractName = String(dmcWorkOrderObject?.contractorName ?? "").trim();

    const hasRepairJobNo = String(workOrderObject?.jobNo ?? "").trim().length > 0;
    const hasDmcJobNo = (() => {
      const j = String(dmcWorkOrderObject?.jobNo ?? "").trim();
      return j.length > 0 && j.endsWith("/1");
    })();



    const isEmaWithTriageContractor =
      !!workOrderObject?.isEMA && (workOrderObject?.contractor === catalystTriageContractorType?.CATALYST_TRIAGE_EMA.toString() ||
        workOrderObject?.contractor === EMAContractorType?.EMA_UNKNOWN_CONTACTDETAILS_TRIAGE.toString() || workOrderObject?.contractor === EMAContractorType?.UNKNOWN_EMA_TRIAGE.toString());



    // Repair WO Failed
    if (woFailureCases && typeof woFailureCases === "object" && Object.keys(woFailureCases).some(k => k.startsWith("repair."))) {
      rows.push({
        referenceRow: { title: t("REF_FAILED_TITLE"), body: workOrderObject.repairReason },
        nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("Repair_Update_WorkOrder_Msg") }
      });
    }
    else if (hasRepairJobNo) {
      const nextDefectNaMsg = (t("NEXT_DEFECT_NA").replace("{DEVELOPER}", repairContractName) +
        (workOrderObject?.isCausingDampAndMould && !hasExistingDMCaseOrWO ? " " + t("APPEND_DMC_TRIAGE") : ""));
      // NA Defect Standard
      if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === false &&
        String(workOrderObject?.contractor ?? "").trim() !== String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim()
      ) {
        const msg = (hasDmcJobNo &&
          workOrderObject?.assessmentInformation?.outcome?.adjustedRiskRating === CasePriority.High &&
          dmcWorkOrderObject?.isEmergency) ?
          (t("NEXT_DEFECT_NA").replace("{DEVELOPER}", repairContractName)) : nextDefectNaMsg;
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // Priority DME, Property Defect, Risk rating is High
      else if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === true &&
        workOrderObject?.isCatalyst === false &&
        String(workOrderObject?.contractor ?? "").trim() !== String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim() &&
        workOrderObject?.priority === RepairPriorityCode.DampAndMouldEmergency &&
        workOrderObject?.assessmentInformation?.outcome?.adjustedRiskRating === CasePriority.High &&
        !hasDmcJobNo
      ) {
        const msg = t("DME_Priority_Defect_Catalyst_Repair").replace("{CONTRACTOR}", repairContractName);
        rows.push({
          referenceRow: { title: t("REF_DMC_Inspection_WO_Ref").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // When Priority DME, Catalyst Defect property single WO and Risk Rating is High
      else if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === true &&
        workOrderObject?.isCatalyst === true &&
        // String(workOrderObject?.contractor ?? "").trim() === String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim() &&
        workOrderObject?.assessmentInformation?.outcome?.adjustedRiskRating === CasePriority.High &&
        workOrderObject?.priority === RepairPriorityCode.DampAndMouldEmergency &&
        !hasDmcJobNo
      ) {
        const msg = t("DME_Priority_Defect_Catalyst_Repair").replace("{CONTRACTOR}", repairContractName);
        rows.push({
          referenceRow: { title: t("REF_DMC_Inspection_WO_Ref").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // EME Defect Standard
      else if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === true &&
        String(workOrderObject?.contractor ?? "").trim() !== String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim()
      ) {
        const msg = t("NEXT_DEFECT_EME") + (workOrderObject?.isCausingDampAndMould && !hasExistingDMCaseOrWO ? " " + t("APPEND_DMC_TRIAGE") : "");
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // Emergency Repair WO With Priority DME DURING OOH FOR AR1 ROLE
      else if (workOrderObject?.isEmergency === true && workOrderObject.isOutOfHours === true && hasViewContractorOOHCommunicationInstruction && workOrderObject?.priority === "DME") {
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: {
            title: t("WHAT_HAPPENS_NEXT"),
            body: t("NEXT_DMC_DME_WITH_ACTOR_OOH").replace("{CONTRACTOR}", repairContractName)
          }
        });
      }
      // Emergency Repair WO
      else if (workOrderObject?.isEmergency === true) {
        if (hasViewContractorOOHCommunicationInstruction === true && workOrderObject?.isOutOfHours === true) {
          rows.push({
            referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
            nextStepsRow: {
              title: t("WHAT_HAPPENS_NEXT"),
              body: t("EME_OOH_AR1_WITH_CONTRACTOR").replace("{CONTRACTOR}", repairContractName)
            }
          });
        }
        else {
          rows.push({
            referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
            nextStepsRow: {
              title: t("WHAT_HAPPENS_NEXT"),
              body: t("EME_REPAIR_TODAY_WITH_CONTRACTOR").replace("{CONTRACTOR}", repairContractName)
            }
          });
        }
      }
      // Appointed WO
      else if (appointmentBundle?.originalWO?.appointmentSlotSelected) {
        const appointmentDisplay = this.buildAppointmentDisplayFromSlot(appointmentBundle.originalWO.appointmentSlotSelected);
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: {
            title: t("WHAT_HAPPENS_NEXT"),
            body: t("NEXT_REPAIR_APPOINTED_WITH_ACTOR_TIME")
              .replace("{CONTRACTOR}", repairContractName)
              .replace("{APPOINTMENT}", appointmentDisplay)
          }
        });
      }
      else if (!workOrderObject?.isPendingAssessment && ButtonRequestAnotherRepairValue) {
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: {
            title: t("WHAT_HAPPENS_NEXT"),
            body: t("Pending_assessment_message")
          }
        });
      }
      // Standard EMA WO Created
      else if (
        workOrderObject?.isEMA === true &&
        String(workOrderObject?.contractor ?? "").trim() !== String(catalystTriageContractorType?.CATALYST_TRIAGE_EMA ?? "").trim()
      ) {
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: {
            title: t("WHAT_HAPPENS_NEXT"),
            body: t("NEXT_EMA_STANDARD_WITH_AGENT").replace("{EMA_CONTRACTOR}", repairContractName)
          }
        });
      }
      // No Suitable Appointment WO / Unappointed WO
      else {
        rows.push({
          referenceRow: { title: t("REF_REPAIR").replace("{JOB_NUMBER}", workOrderObject?.jobNo), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_REPAIR_UNAPPOINTED_WITH_ACTOR").replace("{CONTRACTOR}", repairContractName) }
        });
      }

    } else {
      const nextDefectNaCatalystMsg = (t("NEXT_DEFECT_NA").replace("{DEVELOPER}", repairContractName) + (workOrderObject?.isCausingDampAndMould && !hasExistingDMCaseOrWO ? " " + t("APPEND_DMC_TRIAGE") : ""));
      // Non DM Catalyst Defect property single WO with Low & Medium Risk Rating
      if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === false &&
        workOrderObject?.isCatalyst === true &&
        String(workOrderObject?.contractor ?? "").trim() === String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim() &&
        workOrderObject?.assessmentInformation?.outcome?.adjustedRiskRating !== CasePriority.High &&
        !hasDmcJobNo
      ) {
        const msg = workOrderObject?.priority === RepairPriorityCode.NextAvailable ?
          t("NON_DM_Defect_Catalyst_Repair").replace("{CONTRACTOR}", repairContractName)
          : t("DM_Defect_Catalyst_Repair").replace("{CONTRACTOR}", repairContractName);
        rows.push({
          referenceRow: { title: t("REF_CATALYST_DEFECT_REPAIR"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // NA Defect Catalyst
      else if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === false &&
        String(workOrderObject?.contractor ?? "").trim() === String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim()
      ) {
        const msg = (hasDmcJobNo &&
          workOrderObject?.assessmentInformation?.outcome?.adjustedRiskRating === CasePriority.High &&
          dmcWorkOrderObject?.isEmergency) ?
          (t("NEXT_DEFECT_NA").replace("{DEVELOPER}", repairContractName)) : nextDefectNaCatalystMsg;
        rows.push({
          referenceRow: { title: t("REF_Catalyst_Defect_Triage"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // EME Defect Catalyst
      else if (
        workOrderObject?.isInDefect === true &&
        workOrderObject?.isVandalism === false &&
        workOrderObject?.isEmergency === true &&
        String(workOrderObject?.contractor ?? "").trim() === String(catalystTriageContractorType?.CATALYST_TRIAGE_DEFECT ?? "").trim()
      ) {
        const msg = t("NEXT_DEFECT_EME") + (workOrderObject?.isCausingDampAndMould && !hasExistingDMCaseOrWO ? " " + t("APPEND_DMC_TRIAGE") : "");
        rows.push({
          referenceRow: { title: t("REF_UNDER_REVIEW_TITLE"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: msg }
        });
      }
      // Catalyst EMA WO/ Unknown EMA ContactDetails Triage Created
      else if (
        isEmaWithTriageContractor
      ) {
        rows.push({
          referenceRow: { title: t("REF_UNDER_REVIEW_TITLE"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_EMA_CATALYST") }
        });
      }
      // Triaged Repair – Duplicate
      else if (workOrderObject?.isPotentialDuplicate === true && workOrderObject?.isVulnerable === false) {
        rows.push({
          referenceRow: { title: t("REF_UNDER_REVIEW_TITLE"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_TRIAGED_DUPLICATE") }
        });
      }
      // Triaged Repair – Vulnerable
      else if (workOrderObject?.isPotentialDuplicate === false && workOrderObject?.isVulnerable === true) {
        rows.push({
          referenceRow: { title: t("REF_UNDER_REVIEW_TITLE"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_TRIAGED_VULNERABLE") }
        });
      }
      // Triaged Repair – Combined
      else if (workOrderObject?.isPotentialDuplicate === true && workOrderObject?.isVulnerable === true) {
        rows.push({
          referenceRow: { title: t("REF_UNDER_REVIEW_TITLE"), body: workOrderObject.repairReason },
          nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_TRIAGED_COMBINED") }
        });
      }
    }

    // DMC WO Failed
    if (woFailureCases && typeof woFailureCases === "object" && Object.keys(woFailureCases).some(k => k.startsWith("dmc."))) {
      rows.push({
        referenceRow: { title: t("REF_FAILED_TITLE"), body: t("BODY_DMC") },
        nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("DMC_Failed_CreateWO_Msg") }
      });
    }
    // DMC Inspection WO – DME PRIORITY DURING OOH FOR AR1 ROLE
    else if (hasDmcJobNo && dmcWorkOrderObject?.isEmergency === true && dmcWorkOrderObject.isOutOfHours === true && hasViewContractorOOHCommunicationInstruction) {
      rows.push({
        referenceRow: { title: t("REF_DMC").replace("{DMC_JOB_NUMBER}", dmcWorkOrderObject?.jobNo), body: t("BODY_DMC") },
        nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_DMC_DME_WITH_ACTOR_OOH").replace("{CONTRACTOR}", dmcContractName) }
      });
    }
    // DMC Inspection WO – DM PRIORITY
    else if (hasDmcJobNo && dmcWorkOrderObject?.isEmergency === false) {
      const dmcSlot = appointmentBundle?.dmc?.appointmentSlotSelected;
      const appointedMsg = dmcSlot
        ? t("NEXT_DMC_DM_APPOINTED_WITH_ACTOR_TIME")
          .replace("{CONTRACTOR}", dmcContractName)
          .replace("{APPOINTMENT}", this.buildAppointmentDisplayFromSlot(dmcSlot))
        : t("NEXT_DMC_DM_WITH_ACTOR").replace("{CONTRACTOR}", dmcContractName);
      rows.push({
        referenceRow: { title: t("REF_DMC").replace("{DMC_JOB_NUMBER}", dmcWorkOrderObject?.jobNo), body: t("BODY_DMC") },
        nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: appointedMsg }
      });
    }
    // DMC Inspection WO – DME PRIORITY
    else if (hasDmcJobNo && dmcWorkOrderObject?.isEmergency === true) {
      rows.push({
        referenceRow: { title: t("REF_DMC").replace("{DMC_JOB_NUMBER}", dmcWorkOrderObject?.jobNo), body: t("BODY_DMC") },
        nextStepsRow: { title: t("WHAT_HAPPENS_NEXT"), body: t("NEXT_DMC_DME_WITH_ACTOR").replace("{CONTRACTOR}", dmcContractName) }
      });
    }

    return rows;
  }


  static buildAppointmentDisplayFromSlot(slot?: {
    date?: string;
    availabilityTypeLabel?: string;
    availabilityType?: string;
    start?: string;
    end?: string;
  }): string {
    if (!slot || !slot.date) return "";
    const longDate = moment(slot.date).format("dddd D MMMM YYYY");
    const label = String(slot.availabilityTypeLabel ?? slot.availabilityType ?? "").trim();
    const start = String(slot.start ?? "").trim();
    const end = String(slot.end ?? "").trim();
    const range = start && end ? `${start}–${end}` : (start || end);
    const bracket = label && range ? `${label}, ${range}` : (label || range);
    return bracket ? `${longDate} (${bracket})` : longDate;
  }

  static buildAppointmentBundle(args: {
    original?: {
      id?: string;
      contractorCode?: string;
      contractorName?: string;
      jobNo?: string;
      operationKey?: string;
      calendar: {
        availabilities: any[] | null;
        isPreferences: boolean;
        hasSlaAvailability: boolean;
        earliestAvailabilityType: string | null;
        availabilityStatusReason: string | null;
      } | null;
      isAppointmentRetrievalFailed?: boolean;
      isAppointable?: boolean;
      jobStatus?: string;
      bookingStatus?: string;
    } | null;
    dmc?: {
      id?: string;
      contractorCode?: string;
      contractorName?: string;
      jobNo?: string;
      operationKey?: string;
      calendar: {
        availabilities: any[] | null;
        isPreferences: boolean;
        hasSlaAvailability: boolean;
        earliestAvailabilityType: string | null;
        availabilityStatusReason: string | null;
      } | null;
      isAppointmentRetrievalFailed?: boolean;
      isAppointable?: boolean;
      jobStatus?: string;
      bookingStatus?: string;
    } | null;
  }) {
    const toWO = (src: any | null) => {
      if (!src) return undefined;
      const contractor =
        src.contractorCode || src.contractorName
          ? new Contractor(String(src.contractorCode ?? "").trim(), String(src.contractorName ?? "").trim())
          : undefined;

      const computedJobStatus =
        src.jobStatus ?? (src.jobNo ? "success" : "na");

      const isAppointable =
        typeof src.isAppointable === "boolean"
        && src.isAppointable;

      return new WOEnvelope(
        src.id,
        contractor,
        computedJobStatus,
        src.jobNo,
        isAppointable,
        src.isAppointmentRetrievalFailed ?? false,
        src.operationKey,
        isNullOrUndefined(src?.calendar) ? undefined : new AppointmentAvailability(
          src?.calendar?.isPreferences,
          src?.calendar?.hasSlaAvailability,
          src?.calendar?.earliestAvailabilityType,
          src?.calendar?.statusCode,
          src?.calendar?.availabilities
        )
      );
    };

    return new Bundle(
      toWO(args.original),
      toWO(args.dmc)
    );
  }
}